import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { FocusTrap } from "../../../../common/components/FocusTrap/FocusTrap";
import LeafSortBy from "../../../../common/leaf/LeafFilter/LeafSortBy/LeafSortBy";
import LeafPanel from "../../../../common/leaf/LeafPanel/LeafPanel";
import { Button } from "../../../../common/components/Button/Button";
import { IconClose } from "../../../../common/icons/IconClose/IconClose";
import defaultContent from "../../../../content/actionReports";
import useWindowSize from "../../../../common/hooks/useWindowSize";
import breakpoints from "../../../../common/scss/breakpoints.module.scss";
import "./SortModal.scss";

export type SortDirection = "asc" | "desc";

export type SortModalColumn = {
  kpi: string;
  title: string;
  /** "text" columns render as A-Z/Z-A; all other columns (the default) render as High to Low/Low to High. */
  type?: "text" | "numeric";
};

type Selection = {
  kpi: string;
  direction: SortDirection;
};

export type SortModalProps = {
  show: boolean;
  onClose: () => void;
  columns: SortModalColumn[];
  columnSortState: Record<string, SortDirection | undefined>;
  onApply: (kpi: string, direction: SortDirection) => void;
  /** Excluded from outside-click dismissal, so re-clicking the trigger button can toggle the popup itself. */
  triggerRef?: RefObject<HTMLElement | null>;
};

const OPTION_ID_SEPARATOR = ":";

const getActiveSelection = (columnSortState: Record<string, SortDirection | undefined>) => {
  const activeKpi = Object.keys(columnSortState).find((kpi) => columnSortState[kpi]);
  if (!activeKpi) return undefined;
  return { kpi: activeKpi, direction: columnSortState[activeKpi] as SortDirection };
};

export const SortModal = ({ show, onClose, columns, columnSortState, onApply, triggerRef }: SortModalProps) => {
  const { t } = useTranslation(["actionReports"]);
  const [selection, setSelection] = useState<Selection | undefined>(() => getActiveSelection(columnSortState));
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    if (show) {
      setSelection(getActiveSelection(columnSortState));
    }
    // Only re-seed the selection when the modal transitions to open, so in-progress
    // taps aren't clobbered by columnSortState/columns updates while it's showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Same breakpoint the KPI selector modal uses to switch to its full-page LeafPanel layout
  // (ABOT-13575) — below this width the dropdown popover no longer fits the viewport.
  useLayoutEffect(() => {
    setIsMobileDevice(Number(width) <= Number(breakpoints["tabletSmall"]));
  }, [width]);

  useEffect(() => {
    // LeafPanel owns its own backdrop-click/Escape dismissal on mobile, so this
    // desktop-only outside-click listener would otherwise fight it and double-close.
    if (!show || isMobileDevice) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (triggerRef?.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [show, onClose, triggerRef, isMobileDevice]);

  // The popup is position:absolute below the trigger, so a CSS max-height relative to the
  // viewport alone can't account for how far down the page the trigger sits (a short window
  // with the trigger mid-page can still push the footer past the bottom edge). Measuring the
  // actual top position keeps Cancel/Apply within the popup's own bounds — matching the KPI
  // selector modal — instead of requiring the page itself to scroll to reach them.
  useLayoutEffect(() => {
    if (!show || isMobileDevice) return;

    const updateMaxHeight = () => {
      if (!containerRef.current) return;
      const { top } = containerRef.current.getBoundingClientRect();
      const margin = 16;
      containerRef.current.style.maxHeight = `${Math.max(window.innerHeight - top - margin, 0)}px`;
    };

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [show, isMobileDevice]);

  if (!show) return null;

  const title = t("sortModal.title", defaultContent.sortModal.title).toString();
  const applyLabel = t("sortModal.applyButton", defaultContent.sortModal.applyButton).toString();
  const cancelLabel = t("sortModal.cancelButton", defaultContent.sortModal.cancelButton).toString();
  const closeAriaLabel = t("sortModal.closeAriaLabel", defaultContent.sortModal.closeAriaLabel).toString();

  const getOptionLabel = (column: SortModalColumn, direction: SortDirection): string => {
    const key =
      column.type === "text"
        ? direction === "asc"
          ? "sortModal.textAscending"
          : "sortModal.textDescending"
        : direction === "asc"
          ? "sortModal.numericAscending"
          : "sortModal.numericDescending";
    const defaultValue =
      column.type === "text"
        ? direction === "asc"
          ? defaultContent.sortModal.textAscending
          : defaultContent.sortModal.textDescending
        : direction === "asc"
          ? defaultContent.sortModal.numericAscending
          : defaultContent.sortModal.numericDescending;
    return t(key, defaultValue, { column: column.title }).toString();
  };

  const sortItems = columns.flatMap((column) => {
    // Text columns list A-Z before Z-A; numeric columns list High to Low before Low to High.
    const directionOrder: SortDirection[] = column.type === "text" ? ["asc", "desc"] : ["desc", "asc"];
    return directionOrder.map((direction) => ({
      id: `${column.kpi}${OPTION_ID_SEPARATOR}${direction}`,
      text: getOptionLabel(column, direction),
      selected: selection?.kpi === column.kpi && selection?.direction === direction,
    }));
  });

  const handleRadioButtonClick = (item: { id: string }) => {
    const separatorIndex = item.id.lastIndexOf(OPTION_ID_SEPARATOR);
    const kpi = item.id.slice(0, separatorIndex);
    const direction = item.id.slice(separatorIndex + 1) as SortDirection;
    setSelection({ kpi, direction });
  };

  const handleApply = () => {
    if (!selection) return;
    onApply(selection.kpi, selection.direction);
    onClose();
  };

  const sortOptions = (
    <div className="SortModal__body" data-testid="sort-modal-body">
      <LeafSortBy sort={sortItems} onRadioButtonClick={handleRadioButtonClick} />
    </div>
  );

  const actions = (
    <div className="SortModal__actions">
      <Button
        type="button"
        className="SortModal__button SortModal__button--cancel"
        onClick={onClose}
        data-testid="sort-modal-cancel"
      >
        {cancelLabel}
      </Button>
      <Button
        type="button"
        className="SortModal__button SortModal__button--apply"
        onClick={handleApply}
        disabled={!selection}
        data-testid="sort-modal-apply"
      >
        {applyLabel}
      </Button>
    </div>
  );

  if (isMobileDevice) {
    return (
      <div className="SortModal SortModal--mobile">
        <LeafPanel show={show} onCloseClick={onClose} title={title} id="sort-modal-panel" footer={actions}>
          {sortOptions}
        </LeafPanel>
      </div>
    );
  }

  return (
    <div className="SortModal" ref={containerRef}>
      <FocusTrap
        role="dialog"
        aria-modal={true}
        aria-labelledby="sort-modal-title"
        handleOnClose={onClose}
        className="SortModal__container"
      >
        <div className="SortModal__closeRow">
          <Button
            type="button"
            className="SortModal__closeBtn"
            aria-label={closeAriaLabel}
            onClick={onClose}
            data-testid="sort-modal-close"
          >
            <IconClose width="0.8125rem" height="1.25rem" />
          </Button>
        </div>
        <h2 id="sort-modal-title" className="SortModal__title">
          {title}
        </h2>
        {sortOptions}
        {actions}
      </FocusTrap>
    </div>
  );
};
